import React, { useState } from 'react';
import { Eye, EyeOff, Lock, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ChangePasswordModal = ({ isOpen, onClose, accentColor = '#00d4ff' }) => {
  const { api } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 4) {
      setError('Le nouveau mot de passe doit contenir au moins 4 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
    setError('');
    setSuccess(false);
    onClose();
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="change-password-modal">
      <div className="bg-gray-900 border border-gray-700/50 rounded-2xl w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accentColor}20`, border: `1px solid ${accentColor}40` }}>
              <Lock className="w-5 h-5" style={{ color: accentColor }} />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Modifier le mot de passe</h3>
              <p className="text-xs text-gray-500">Saisissez votre ancien et nouveau mot de passe</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white" data-testid="change-password-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="px-4 py-2.5 rounded-lg text-xs font-medium" data-testid="change-password-error"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
              {error}
            </div>
          )}

          {success && (
            <div className="px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2" data-testid="change-password-success"
              style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', color: '#4ade80' }}>
              <Check className="w-4 h-4" />
              Mot de passe modifie avec succes !
            </div>
          )}

          {/* Old password */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-medium">Ancien mot de passe</label>
            <div className="relative">
              <input
                type={showOld ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none transition-all"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = `${accentColor}50`; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                placeholder="Votre mot de passe actuel"
                required
                data-testid="change-password-old"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-medium">Nouveau mot de passe</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none transition-all"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = `${accentColor}50`; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                placeholder="Minimum 4 caractères"
                required
                data-testid="change-password-new"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-medium">Confirmer le nouveau mot de passe</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none transition-all"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = `${accentColor}50`; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                placeholder="Retapez le nouveau mot de passe"
                required
                data-testid="change-password-confirm"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || success}
            className="w-full py-3 rounded-lg font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: accentColor, color: '#000' }}
            data-testid="change-password-submit"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> Modification...</>
            ) : (
              <><Lock className="w-4 h-4" /> Modifier le mot de passe</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
